import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { deriveNextStep } from "@/lib/nextStep";
import { CheckCircle2, Loader2, HeartHandshake, ArrowRight } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState("checking");
  const [plan, setPlan] = useState(null);
  const [requiresBooking, setRequiresBooking] = useState(false);
  const [booking, setBooking] = useState(null);
  const [nextStep, setNextStep] = useState(null); // prochaine étape déterminée depuis l'état réel
  const sessionId = params.get("session_id");
  const returnTo = params.get("return_to");

  // Détermine la prochaine étape depuis l'état RÉEL (bilan/programme/cycle). Ne génère rien.
  const resolveNextStep = async () => {
    try {
      const [p, prog, cs] = await Promise.all([
        api.get("/profile"),
        api.get("/program"),
        api.get("/program/cycle-status").catch(() => ({ data: {} })),
      ]);
      setNextStep(deriveNextStep({ profile: p.data.profile, program: prog.data, cycle: cs.data }));
    } catch {
      // État non déterminable de façon fiable -> orientation générique vers l'espace.
      setNextStep({ key: "fallback", label: "Accéder à mon espace", route: returnTo || "/app", dashboard: null });
    }
  };

  useEffect(() => {
    let tries = 0;
    const poll = async () => {
      if (!sessionId) return setStatus("error");
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setPlan(r.data.plan);
          setRequiresBooking(!!r.data.requires_booking);
          if (r.data.requires_booking) {
            const cur = await api.get("/booking/current").catch(() => ({ data: {} }));
            setBooking(cur.data?.booking || null);
          }
          await checkAuth();
          setStatus("paid");
          if (!r.data.requires_booking) resolveNextStep();
          return;
        }
      } catch {}
      tries += 1;
      if (tries > 10) return setStatus("timeout");
      setTimeout(poll, 2000);
    };
    poll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, checkAuth]);

  const isPremium = plan === "premium";

  return (
    <div className="min-h-screen bg-sand flex items-center justify-center px-6">
      <div className="max-w-md text-center fade-up">
        {status === "checking" && (<><Loader2 size={40} className="animate-spin text-sage mx-auto" /><p className="mt-6 text-obsidian/60">Confirmation du paiement…</p></>)}
        {status === "paid" && (
          <>
            <span className="animate-pop mx-auto flex w-20 h-20 rounded-full bg-sage/12 items-center justify-center">
              <CheckCircle2 size={44} className="text-sage" strokeWidth={1.6} />
            </span>
            {requiresBooking ? (
              <>
                <h1 className="fade-up-2 mt-7 text-3xl font-light tracking-tight">Rendez-vous confirmé 🎉</h1>
                {booking ? (
                  <>
                    <p className="fade-up-3 mt-3 text-obsidian/60">Ton créneau est réservé et ton paiement confirmé. Un email de confirmation vient de t'être envoyé.</p>
                    <div data-testid="rdv-recap" className="fade-up-4 mt-6 rounded-2xl border border-border bg-white p-5 text-left">
                      <div className="text-sm"><span className="text-obsidian/50">Créneau : </span><span className="font-medium">{booking.slot_label}</span></div>
                      {booking.offer_label && <div className="mt-1.5 text-sm"><span className="text-obsidian/50">Prestation : </span><span className="font-medium">{booking.offer_label}</span></div>}
                      <div className="mt-1.5 text-sm"><span className="text-obsidian/50">Format : </span><span className="font-medium">{booking.mode === "phone" ? "Téléphone" : "Visio (Google Meet)"}</span></div>
                      {booking.meet_link ? (
                        <a data-testid="rdv-meet-link" href={booking.meet_link} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-sage hover:underline"><HeartHandshake size={15} /> Rejoindre en visio (Google Meet)</a>
                      ) : booking.mode === "phone" ? (
                        <p className="mt-3 text-sm text-obsidian/60">Ton Coach t'appellera au créneau choisi.</p>
                      ) : (
                        <p className="mt-3 text-sm text-obsidian/60">Le lien de visio te sera transmis par email.</p>
                      )}
                    </div>
                  </>
                ) : (
                  <p className="fade-up-3 mt-3 text-obsidian/60">Ton paiement est confirmé. Les détails de ton rendez-vous arrivent par email.</p>
                )}
                <div className="fade-up-4 mt-8 flex flex-col gap-3">
                  <button data-testid="payment-continue" onClick={() => navigate(returnTo || "/app")}
                    className="rounded-full bg-obsidian text-white px-7 py-4 font-medium hover:-translate-y-0.5 transition-transform">Accéder à mon espace</button>
                  <button data-testid="payment-profile" onClick={() => navigate("/profil")}
                    className="rounded-full border border-obsidian px-7 py-3 font-medium hover:bg-obsidian hover:text-white transition-[background-color,color]">Voir mon profil</button>
                </div>
              </>
            ) : (
              <>
                <h1 className="fade-up-2 mt-7 text-3xl font-light tracking-tight">Votre accès est activé</h1>
                <p className="fade-up-3 mt-3 text-obsidian/60">Tout est prêt. Vous pouvez maintenant poursuivre votre préparation avec MAI.</p>
                {nextStep ? (
                  <>
                    <div className="fade-up-4 mt-8 flex flex-col gap-3">
                      <button data-testid="payment-continue" data-next-step={nextStep.key} onClick={() => navigate(nextStep.route)}
                        className="rounded-full bg-obsidian text-white px-7 py-4 font-medium inline-flex items-center justify-center gap-2 hover:-translate-y-0.5 transition-transform">
                        {nextStep.label} <ArrowRight size={17} />
                      </button>
                      {isPremium && (
                        <button data-testid="payment-schedule" onClick={() => navigate("/reservation")}
                          className="rounded-full border border-obsidian px-7 py-3 font-medium inline-flex items-center justify-center gap-2 hover:bg-obsidian hover:text-white transition-[background-color,color]">
                          <HeartHandshake size={16} /> Planifier mon suivi humain</button>
                      )}
                    </div>
                    <p className="fade-up-4 mt-4 text-sm text-obsidian/45">Vous retrouverez ensuite toutes vos séances dans « Mon programme ».</p>
                  </>
                ) : (
                  <p data-testid="payment-preparing" className="fade-up-4 mt-8 inline-flex items-center justify-center gap-2 text-obsidian/60">
                    <Loader2 size={16} className="animate-spin text-sage" /> Votre accès est activé. Nous préparons la suite…
                  </p>
                )}
              </>
            )}
          </>
        )}
        {(status === "error" || status === "timeout") && (
          <>
            <h1 className="text-2xl font-light">Paiement en attente</h1>
            <p className="mt-3 text-obsidian/60">Si le montant a été débité, ton offre sera activée sous peu.</p>
            <button onClick={() => navigate("/app")} className="mt-6 rounded-full border border-obsidian px-6 py-3 font-medium">Retour à mon espace</button>
          </>
        )}
      </div>
    </div>
  );
}
